import { useQuery } from "@tanstack/react-query";
import {
  Mail,
  Bell,
  Megaphone,
  Shield,
  Gavel,
  FileText,
  Clock,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface NotificationItem {
  type: string;
  id: number;
  title: string;
  description: string | null;
  date: string;
  severity: "info" | "warning" | "critical";
  personId?: number;
  personName?: string;
}

interface MessageCenterData {
  unreadMessagesCount: number;
  activeRemindersCount: number;
  announcementsCount: number;
  expiringOrdersCount: number;
  upcomingHearingsCount: number;
  expiringReleasesCount: number;
  notifications: NotificationItem[];
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const severityColors: Record<string, string> = {
  info: "bg-blue-100 text-blue-700",
  warning: "bg-amber-100 text-amber-700",
  critical: "bg-red-100 text-red-700",
};

const typeIcons: Record<string, React.ElementType> = {
  message: Mail,
  reminder: Clock,
  announcement: Megaphone,
  protection_order: Shield,
  court_hearing: Gavel,
  info_release: FileText,
};

const typeLabels: Record<string, string> = {
  message: "Message",
  reminder: "Reminder",
  announcement: "Announcement",
  protection_order: "Protection Order",
  court_hearing: "Court Hearing",
  info_release: "Info Release",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MessageCenter() {
  const { data: mcData, isLoading } = useQuery<{ data: MessageCenterData }>({
    queryKey: ["message-center"],
    queryFn: () =>
      api.get<{ data: MessageCenterData }>("/message-center"),
  });

  const mc = mcData?.data;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-20 animate-pulse rounded-lg border bg-muted"
          />
        ))}
      </div>
    );
  }

  if (!mc) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Bell className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">
            Unable to load notifications.
          </p>
        </CardContent>
      </Card>
    );
  }

  const totalCount =
    mc.unreadMessagesCount +
    mc.activeRemindersCount +
    mc.expiringOrdersCount +
    mc.upcomingHearingsCount +
    mc.expiringReleasesCount;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <SummaryCard
          label="Unread Messages"
          count={mc.unreadMessagesCount}
          icon={Mail}
          color="text-blue-600"
        />
        <SummaryCard
          label="Active Reminders"
          count={mc.activeRemindersCount}
          icon={Clock}
          color="text-amber-600"
        />
        <SummaryCard
          label="Announcements"
          count={mc.announcementsCount}
          icon={Megaphone}
          color="text-purple-600"
        />
        <SummaryCard
          label="Expiring Orders"
          count={mc.expiringOrdersCount}
          icon={Shield}
          color="text-red-600"
        />
        <SummaryCard
          label="Court Hearings"
          count={mc.upcomingHearingsCount}
          icon={Gavel}
          color="text-orange-600"
        />
        <SummaryCard
          label="Expiring Releases"
          count={mc.expiringReleasesCount}
          icon={FileText}
          color="text-teal-600"
        />
      </div>

      {/* Notifications list */}
      {mc.notifications.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Bell className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No active notifications.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Notifications ({totalCount})
            </CardTitle>
            <CardDescription>
              Items requiring your attention in the next 7 days.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y">
              {mc.notifications.map((n, idx) => {
                const Icon = typeIcons[n.type] ?? Bell;
                return (
                  <div
                    key={`${n.type}-${n.id}-${idx}`}
                    className="flex items-start gap-3 px-4 py-3"
                  >
                    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                            severityColors[n.severity] ?? severityColors.info,
                          )}
                        >
                          {typeLabels[n.type] ?? n.type}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(n.date).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="mt-0.5 text-sm font-medium">{n.title}</p>
                      {n.description && (
                        <p className="text-xs text-muted-foreground">
                          {n.description}
                        </p>
                      )}
                      {n.personName && (
                        <p className="text-xs text-muted-foreground">
                          Client: {n.personName}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Summary card
// ---------------------------------------------------------------------------

function SummaryCard({
  label,
  count,
  icon: Icon,
  color,
}: {
  label: string;
  count: number;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <Icon className={cn("h-5 w-5 shrink-0", color)} />
        <div>
          <p className="text-2xl font-bold">{count}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}
